<template>
    <v-dialog v-model="dialog" width="80%">
        <v-card class="theme--dark">
            <v-card-title class="justify-space-between py-0">
                <span v-if="imagens && imagens.length">
                    {{ carouselIndex + 1 }} / {{ imagens.length }}
                </span>
                <span v-else>
                    {{ titulo }}
                </span>
                <v-btn icon @click="close" aria-label="Fechar">
                    <v-icon class="white--text">close</v-icon>
                </v-btn>
            </v-card-title>

            <v-divider></v-divider>

            <v-card class="py-0 theme--dark" style="box-shadow: none">
                <v-progress-linear
                    v-if="loading"
                    indeterminate
                    class="mb-3"
                />

                <div v-if="!loading && (!imagens || !imagens.length)" class="text-sm-center pa-3">
                    <span>Nenhuma imagem para exibir.</span>
                </div>

                <div v-else>
                    <v-carousel
                        style="box-shadow: none"
                        class="py-0"
                        v-model="carouselIndex"
                        :cycle="false"
                        hide-delimiters
                        height="470px"
                    >
                        <v-carousel-item
                            class="px-5"
                            v-for="(imagem, i) in imagens"
                            :key="i"
                            :src="resolverSrcImagem(imagem.base64)"
                            contain
                        />
                    </v-carousel>

                    <div v-if="imagens.length > 1" class="az-thumbnails-bar">
                        <v-btn
                            :style="{ visibility: (possuiOverflowMiniaturas && podeRolarMiniaturasEsquerda) ? 'visible' : 'hidden' }"
                            icon
                            class="az-thumbnails-arrow az-thumbnails-arrow--left theme--dark"
                            @click="scrollThumbs('left')"
                            aria-label="Ver miniaturas anteriores"
                            title="Anterior"
                        >
                            <v-icon>chevron_left</v-icon>
                        </v-btn>

                        <div
                            ref="thumbStrip"
                            class="az-thumbnails-wrap"
                            aria-label="Miniaturas"
                            @scroll="atualizarSetasMiniaturas"
                            @wheel.prevent="onThumbWheel"
                        >
                            <div
                                v-for="(imagem, i) in imagens"
                                :key="`thumb-${i}`"
                                class="az-thumbnail"
                                :class="{ 'az-thumbnail--active': i === carouselIndex }"
                                @click="carouselIndex = i"
                            >
                                <img
                                    class="az-thumbnail-img"
                                    :src="resolverSrcImagem(imagem.base64)"
                                    :alt="`Miniatura ${i + 1}`"
                                />
                            </div>
                        </div>

                        <v-btn
                            :style="{ visibility: (possuiOverflowMiniaturas && podeRolarMiniaturasDireita) ? 'visible' : 'hidden' }"
                            icon
                            class="az-thumbnails-arrow az-thumbnails-arrow--right theme--dark"
                            @click="scrollThumbs('right')"
                            aria-label="Ver próximas miniaturas"
                            title="Próximo"
                        >
                            <v-icon>chevron_right</v-icon>
                        </v-btn>
                    </div>
                </div>
            </v-card>

            <v-divider></v-divider>

            <v-card-actions v-if="podeEditar" class="justify-space-between">
                <v-btn title="Adicionar Imagens" color="primary" @click.stop="adicionarImagem">
                    <v-icon>add_a_photo</v-icon>
                </v-btn>
                <v-btn v-if="imagens.length" title="Excluir Imagem" color="error" @click.stop="excluirImagem">
                    <v-icon>delete</v-icon>
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

</template>

<script>
    export default {
        name: 'ImagemCarouselDialog',
        props: {
            titulo: {
                type: String,
                default: 'Imagens do item'
            },
            fetcher: {
                type: Function,
                default: null
            },
            fetchAction: {
                type: String,
                default: ''
            },
            defaultMime: {
                type: String,
                default: 'image/jpeg'
            },
            useCache: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                dialog: false,
                loading: false,
                imagens: [],
                carouselIndex: 0,
                currentItemId: null,
                cachePorItemId: {},
                podeEditar: false,

                animacaoRodaMouseId: null,
                acumuladorRodaMouse: 0,
                possuiOverflowMiniaturas: false,
                podeRolarMiniaturasEsquerda: false,
                podeRolarMiniaturasDireita: false,
                manipuladorRedimensionamentoMiniaturas: null
            }
        },
        watch: {
            imagens() {
                this.$nextTick(() => {
                    this.atualizarSetasMiniaturas()
                    this.scrollToActiveThumb()
                })
            },
            dialog(dialogAberto) {
                if (dialogAberto) {
                    this.$nextTick(() => {
                        this.atualizarSetasMiniaturas()
                        this.scrollToActiveThumb()
                    })
                }
            },
            carouselIndex() {
                this.scrollToActiveThumb()
            }
        },
        mounted() {
            this.manipuladorRedimensionamentoMiniaturas = () => this.atualizarSetasMiniaturas()
            window.addEventListener('resize', this.manipuladorRedimensionamentoMiniaturas, { passive: true })
        },
        beforeDestroy() {
            if (this.animacaoRodaMouseId) {
                cancelAnimationFrame(this.animacaoRodaMouseId)
                this.animacaoRodaMouseId = null
            }
            if (this.manipuladorRedimensionamentoMiniaturas) {
                window.removeEventListener('resize', this.manipuladorRedimensionamentoMiniaturas)
                this.manipuladorRedimensionamentoMiniaturas = null
            }
        },
        methods: {
            async open({ itemId, index = 0, podeEditar = false } = {}) {
                this.dialog = true
                this.carouselIndex = Number.isFinite(index) ? index : 0
                this.currentItemId = itemId || null
                this.podeEditar = podeEditar
                this.imagens = []

                if (!this.currentItemId) return

                if (this.useCache) {
                    const cacheDoItem = this.cachePorItemId[this.currentItemId]
                    if (cacheDoItem && cacheDoItem.length) {
                        this.imagens = cacheDoItem
                        return
                    }
                }

                this.loading = true
                const itemIdLocal = this.currentItemId

                try {
                    const lista = await this.buscarImagensBase64(itemIdLocal)

                    if (this.currentItemId !== itemIdLocal) return

                    const imagensArray = Array.isArray(lista) ? lista : []
                    if (this.useCache) {
                        this.$set(this.cachePorItemId, itemIdLocal, imagensArray)
                    }
                    this.imagens = imagensArray
                } catch (e) {
                    if (this.currentItemId === itemIdLocal) {
                        this.imagens = []
                    }
                } finally {
                    if (this.currentItemId === itemIdLocal) {
                        this.loading = false
                    }
                }
            },

            async refresh() {
                if (!this.currentItemId) return

                this.loading = true
                try {
                    const lista = await this.buscarImagensBase64(this.currentItemId)
                    const imagensArray = Array.isArray(lista) ? lista : []

                    if (this.useCache) {
                        this.$set(this.cachePorItemId, this.currentItemId, imagensArray)
                    }
                    this.imagens = imagensArray

                    if (this.imagens.length > 0) {
                        this.carouselIndex = this.imagens.length - 1
                    }
                    this.$nextTick(() => {
                        this.scrollToActiveThumb()
                    })
                } catch (e) {
                    this.imagens = []
                } finally {
                    this.loading = false
                }
            },

            close() {
                this.dialog = false
                this.loading = false
                this.imagens = []
                this.carouselIndex = 0
                this.currentItemId = null
                this.podeEditar = false
            },

            adicionarImagem() {
                this.$emit('adicionarImagem', this.currentItemId)
            },

            async excluirImagem() {
                const imagemAtual = this.imagens[this.carouselIndex]
                if (imagemAtual && imagemAtual.id) {
                    this.$emit('excluirImagem', {
                        idImagem: imagemAtual.id,
                        callback: () => {
                            this.imagens.splice(this.carouselIndex, 1)
                            if (this.carouselIndex >= this.imagens.length) {
                                this.carouselIndex = Math.max(0, this.imagens.length - 1)
                            }
                            if (this.useCache) {
                                this.$set(this.cachePorItemId, this.currentItemId, [...this.imagens])
                            }
                        }
                    })
                }
            },

            async buscarImagensBase64(itemId) {
                if (this.fetcher) {
                    return await this.fetcher(itemId)
                }
                if (!this.$store || !this.fetchAction) {
                    return []
                }
                return await this.$store.dispatch(this.fetchAction, { itemId })
            },

            resolverSrcImagem(base64) {
                if (!base64) return ''
                return `data:${this.defaultMime};base64,${base64}`
            },

            atualizarSetasMiniaturas() {
                const elementoMiniaturas = this.$refs.thumbStrip
                if (!elementoMiniaturas) {
                    this.possuiOverflowMiniaturas = false
                    this.podeRolarMiniaturasEsquerda = false
                    this.podeRolarMiniaturasDireita = false
                    return
                }

                const existeOverflow = elementoMiniaturas.scrollWidth > elementoMiniaturas.clientWidth + 1
                const maximoScrollEsquerda = elementoMiniaturas.scrollWidth - elementoMiniaturas.clientWidth
                const TOLERANCIA = 2

                this.possuiOverflowMiniaturas = existeOverflow
                this.podeRolarMiniaturasEsquerda = existeOverflow && elementoMiniaturas.scrollLeft > TOLERANCIA
                this.podeRolarMiniaturasDireita = existeOverflow && elementoMiniaturas.scrollLeft < (maximoScrollEsquerda - TOLERANCIA)
            },

            scrollThumbs(direction) {
                const elementoMiniaturas = this.$refs.thumbStrip
                if (!elementoMiniaturas) return

                const passo = Math.max(160, Math.floor(elementoMiniaturas.clientWidth * 0.8))
                const deslocamento = direction === 'left' ? -passo : passo

                elementoMiniaturas.scrollBy({ left: deslocamento, behavior: 'smooth' })

                requestAnimationFrame(() => this.atualizarSetasMiniaturas())
            },

            onThumbWheel(eventoRodaMouse) {
                const elementoMiniaturas = this.$refs.thumbStrip
                if (!elementoMiniaturas) return

                let deslocamentoBruto = Math.abs(eventoRodaMouse.deltaX) > Math.abs(eventoRodaMouse.deltaY)
                    ? eventoRodaMouse.deltaX
                    : eventoRodaMouse.deltaY

                if (eventoRodaMouse.deltaMode === 1) deslocamentoBruto *= 16

                const VELOCIDADE = 1.0
                const deslocamento = deslocamentoBruto * VELOCIDADE

                this.acumuladorRodaMouse += deslocamento

                if (this.animacaoRodaMouseId) return
                this.animacaoRodaMouseId = requestAnimationFrame(() => {
                    elementoMiniaturas.scrollLeft += this.acumuladorRodaMouse

                    this.acumuladorRodaMouse = 0
                    this.animacaoRodaMouseId = null

                    this.atualizarSetasMiniaturas()
                })
            },

            scrollToActiveThumb() {
                this.$nextTick(() => {
                    const container = this.$refs.thumbStrip
                    if (!container) return

                    const activeThumb = container.querySelector('.az-thumbnail--active')
                    if (!activeThumb) return

                    const containerWidth = container.clientWidth
                    const thumbWidth = activeThumb.clientWidth
                    const thumbLeft = activeThumb.offsetLeft
                    const scrollLeft = container.scrollLeft

                    const isVisible = (thumbLeft >= scrollLeft) && (thumbLeft + thumbWidth <= scrollLeft + containerWidth)

                    if (!isVisible) {
                        const targetScroll = thumbLeft - (containerWidth / 2) + (thumbWidth / 2)
                        container.scrollTo({
                            left: targetScroll,
                            behavior: 'smooth'
                        })
                    }
                })
            }
        }
    }
</script>

<style scoped lang="stylus">
    .az-thumbnails-bar
        position: relative
        display: flex
        align-items: center
        justify-content: center
        padding: 6px 0

    .az-thumbnails-wrap
        display: flex
        flex-wrap: nowrap
        gap: 10px
        justify-content: flex-start
        align-items: center
        overflow-x: auto
        overflow-y: hidden
        max-width: 100%
        padding: 6px 6px
        scroll-behavior: smooth
        -webkit-overflow-scrolling: touch

        scrollbar-width: thin
        scrollbar-color: rgba(255,255,255,.35) transparent

    .az-thumbnails-wrap::-webkit-scrollbar
        height: 6px

    .az-thumbnails-wrap::-webkit-scrollbar-track
        background: transparent

    .az-thumbnails-wrap::-webkit-scrollbar-thumb
        background-color: rgba(255,255,255,.35)
        border-radius: 999px

    .az-thumbnails-wrap::-webkit-scrollbar-thumb:hover
        background-color: rgba(255,255,255,.55)


    .az-thumbnails-arrow
        background: rgba(0, 0, 0, 0.1)
        &--left
          left: 0
        &--right
          right: 0

    .az-thumbnail
        width: 80px
        height: 80px
        flex: 0 0 auto
        cursor: pointer
        border: 2px solid transparent
        border-radius: 4px
        overflow: hidden
        transition: border-color 0.2s ease-in-out

        &--active
            border-color: var(--v-primary-base, #1976D2)

    .az-thumbnail-img
        width: 100%
        height: 100%
        object-fit: cover
</style>
